import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { faLinkSlash } from '@fortawesome/pro-light-svg-icons/faLinkSlash';
import { TypedMemo } from '../../../common/types';
import { ConfirmDialog } from '../../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogIconMark } from '../../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { useGetSongPublishesQuery } from '../../../../redux/api/fymAPI';
import { ServiceConnection } from '../../../../redux/api/fymAPI.types';
import { soundCloudCardMessages as m } from './soundCloudCardMessages';
import { waitingSoundCloudUploads } from './waitingSoundCloudUploads';

interface SoundCloudDisconnectModalProps {
  accountId: number;
  connection: ServiceConnection;
  isOpen: boolean;
  isBusy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Disconnect SoundCloud (design a4): the existing confirm dialog with the SoundCloud copy. The
 * waiting uploads of the card (`waitingUploads`) are named; Disconnect cancels them (spec D11).
 */
const SoundCloudDisconnectModalComponent: React.FC<SoundCloudDisconnectModalProps> = ({
  accountId,
  connection,
  isOpen,
  isBusy,
  onCancel,
  onConfirm,
}) => {
  const intl = useIntl();
  const count = connection.waitingUploads ?? 0;
  const { data } = useGetSongPublishesQuery({ accountId }, { skip: !isOpen || count === 0 });
  const songs = useMemo(() => waitingSoundCloudUploads(data?.publishes), [data]);
  const list = songs
    .map((one) =>
      intl.formatMessage(m.disconnectSong, {
        title: one.title,
        date:
          one.publishAt === null
            ? ''
            : intl.formatDate(one.publishAt, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
      })
    )
    .join(', ');

  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="danger"
      mark={<ConfirmDialogIconMark tone="danger" icon={faLinkSlash} />}
      title={intl.formatMessage(m.disconnectTitle)}
      description={intl.formatMessage(m.disconnectBody)}
      cancelLabel={intl.formatMessage(m.disconnectKeep)}
      confirmLabel={
        count === 0 ? intl.formatMessage(m.disconnectConfirm) : intl.formatMessage(m.disconnectConfirmCancel, { count })
      }
      isBusy={isBusy}
      onConfirm={onConfirm}
      onCancel={onCancel}
      testId="soundcloud-disconnect"
    >
      {count > 0 && (
        <Alert color="danger" className="mb-0 small">
          <b>{intl.formatMessage(m.disconnectWaiting, { count })}</b>{' '}
          {list === ''
            ? intl.formatMessage(m.disconnectWaitingCancels, { count })
            : intl.formatMessage(m.disconnectWaitingCancelsList, { count, songs: list })}
        </Alert>
      )}
      {connection.artist !== null && (
        <div className="d-flex align-items-center gap-2 mt-2">
          {connection.artist.imageUrl !== null && connection.artist.imageUrl !== '' && (
            <img src={connection.artist.imageUrl} alt="" width={32} height={32} className="rounded-circle" />
          )}
          <b>{connection.artist.name}</b>
        </div>
      )}
    </ConfirmDialog>
  );
};

export const SoundCloudDisconnectModal = TypedMemo(SoundCloudDisconnectModalComponent);
